import { memo, useEffect, useState } from 'react';

import { cx } from '@/utils/cx';

import styles from './LoopSpinner.module.scss';

/**
 * NOL 디자인 시스템의 LoopSpinner 를 core-ui 내부에 소스 복사한 버전.
 *
 * 원본: `@yanolja-rnd/nol-design-system/loop-spinner`
 * 복사 이유: core-ui 의 zero runtime-dependency 원칙. 외부 패키지 import 금지.
 *
 * 변경점:
 * - tailwind utility → SCSS module
 * - class-variance-authority 의존 제거 (단순 size enum)
 * - 반응형 size 옵션 제거 (mobile/pc 구분 없음 — YAGNI). 진짜 필요해지면 그때 도입
 *
 * 동기화 정책: 원본의 시각 디자인 변경 시 (예: stroke width, gradient color) 수동 sync.
 * 변경 감지: design system PR review 시 spinner 변경 사항 확인. 자동 sync 메커니즘 없음.
 *
 * 24×24 px 기준 설계:
 *   - stroke-width: 3.6px (inside alignment)
 *   - circle radius: 12 - 3.6/2 = 10.2px
 *   - head dot: Ø 3.6px, 12시 방향 (그라데이션 시작점)
 *   - animation: 1s linear infinite spin
 */
export interface LoopSpinnerProps {
  size?: 'medium' | 'large';
  /**
   * 노출 지연(ms). 기본 300ms.
   *
   * 도허티 임계값 (400ms) 이내 응답 시 spinner 안 보이게 → flash 회피.
   * 빠른 네트워크 환경에서 사용자 피로도 감소.
   */
  delay?: number;
  className?: string;
}

export const LoopSpinner = memo(function LoopSpinner({ size = 'medium', delay = 300, className }: LoopSpinnerProps) {
  const [visible, setVisible] = useState(delay === 0);

  useEffect(() => {
    if (delay === 0) return undefined;
    const timer = setTimeout(() => {
      setVisible(true);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [delay]);

  if (!visible) return null;

  const sizeClass = size === 'large' ? styles.sizeLarge : styles.sizeMedium;

  return (
    <svg
      aria-busy="true"
      aria-label="loading"
      className={cx(styles.spinner, sizeClass, className)}
      fill="none"
      role="status"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient gradientUnits="userSpaceOnUse" id="core-ui-loop-spinner-top" x1="12" x2="12" y1="22.2" y2="1.8">
          <stop offset="0%" stopColor="#4154FF" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#4154FF" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id="core-ui-loop-spinner-bottom"
          x1="12"
          x2="12"
          y1="1.8"
          y2="22.2"
        >
          <stop offset="0%" stopColor="#4154FF" stopOpacity="1" />
          <stop offset="100%" stopColor="#4154FF" stopOpacity="0.5" />
        </linearGradient>
      </defs>

      {/* 하단 반원: 6시 → 12시 (반투명 → 투명) */}
      <path
        d="M 12 22.2 A 10.2 10.2 0 0 1 12 1.8"
        stroke="url(#core-ui-loop-spinner-bottom)"
        strokeLinecap="butt"
        strokeWidth="3.6"
      />

      {/* 상단 반원: 12시 → 6시 (불투명 → 반투명) */}
      <path
        d="M 12 1.8 A 10.2 10.2 0 0 1 12 22.2"
        stroke="url(#core-ui-loop-spinner-top)"
        strokeLinecap="butt"
        strokeWidth="3.6"
      />

      {/* 헤드 닷: 12시 방향 (그라데이션 시작점) */}
      <circle cx="12" cy="1.8" fill="#4154FF" r="1.8" />
    </svg>
  );
});
